/**
 * toggleHeader.ts - Toggle a table's header row / column.
 *
 * Exposes the public {@link toggleHeaderCol} / {@link toggleHeaderRow} methods (orchestration:
 * persistence) and the pure {@link _toggleHeaderColTransform} / {@link _toggleHeaderRowTransform}
 * they wrap.
 */

import produce from 'immer';

import {type Table} from 'notebook/tables/constants/TableConstants';
import {type TableMutationResult} from 'notebook/tables/excess/ops/TableOpConstants';
import {persistTable, type TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';

/**
 * Toggle the header column flag on the table, clearing bold formatting on the first column, then
 * persist the change.
 */
export function toggleHeaderCol(params: TableMutationParams): void {
  const {table, index, updateHandler} = params.getTableModel().props;
  persistTable(updateHandler, index, table.name, _toggleHeaderColTransform(table));
}

/**
 * Toggle the header row flag on the table, clearing bold formatting on the first row, then persist
 * the change.
 */
export function toggleHeaderRow(params: TableMutationParams): void {
  const {table, index, updateHandler} = params.getTableModel().props;
  persistTable(updateHandler, index, table.name, _toggleHeaderRowTransform(table));
}

/**
 * Toggle the header column flag on a table.
 *
 * Flips `table.headerCol` and clears any bold formatting on the cells in the first
 * column (the column that becomes / stops being the header). Returns a `noop` table op
 * since toggling the header does not require spreadsheet evaluation.
 *
 * @param table - The current table
 * @returns The mutation result with the new table and a `noop` operation
 */
export function _toggleHeaderColTransform(table: Table): TableMutationResult {
  const newTable = produce(table, (draftTable) => {
    draftTable.headerCol = !draftTable.headerCol;
    // Reset bold of the first column when toggling the header column
    draftTable.table.forEach((draftRow) => {
      const firstCell = draftRow[0];
      if (firstCell != null) {
        delete firstCell.bold;
      }
    });
  });

  return {
    table: newTable,
    tableOp: {type: 'noop'},
    affectedCells: [],
  };
}

/**
 * Toggle the header row flag on a table.
 *
 * Flips `table.headerRow` and clears any bold formatting on the cells in the first
 * row (the row that becomes / stops being the header). Returns a `noop` table op
 * since toggling the header does not require spreadsheet evaluation.
 *
 * @param table - The current table
 * @returns The mutation result with the new table and a `noop` operation
 */
export function _toggleHeaderRowTransform(table: Table): TableMutationResult {
  const newTable = produce(table, (draftTable) => {
    draftTable.headerRow = !draftTable.headerRow;
    // Reset bold of the first row when toggling the header row. Guard against empty
    // tables (the Table type permits `table: []`).
    draftTable.table[0]?.forEach((cell) => {
      delete cell.bold;
    });
  });

  return {
    table: newTable,
    tableOp: {type: 'noop'},
    affectedCells: [],
  };
}
